import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { subjectStyle, fmtDue, ukToday } from "@/lib/subjects";

const DayCol = ({ d, hw }) => {
  const today = d.date === ukToday();
  return (
    <section data-testid={`week-day-${d.day.toLowerCase()}`} className={`min-w-0 rounded-2xl border p-3 ${today ? "border-gold-400 bg-gold-400/5" : "border-slate-200 bg-surface"}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-bold text-slate-900">{fmtDue(d.date)}</p>
        {d.week && <span className="rounded-full border border-gold-300 bg-gold-100 px-2 py-0.5 font-mono text-[10px] font-bold text-gold-900">{d.week}</span>}
      </div>
      {hw.length > 0 && (
        <ul className="mt-2 space-y-1">
          {hw.map((h) => (
            <li key={h.hw_id} className={`rounded-lg px-2 py-1 text-xs font-semibold ${h.kind === "test" ? "bg-brand text-white" : "bg-navy-600 text-white"} ${h.done ? "opacity-50 line-through" : ""}`}>{h.kind === "test" ? "Test: " : "Due: "}{h.title}</li>
          ))}
        </ul>
      )}
      {d.closed ? <p className="mt-2 rounded-lg bg-orange-50 border border-orange-200 px-2 py-1.5 text-xs font-semibold text-orange-800">{d.closed}</p> : (
        <ul className="mt-2 space-y-1">
          {(d.lessons ?? []).map((l, i) => (
            <li key={i} className="flex items-center gap-2 text-xs"><span className="w-9 shrink-0 font-mono text-slate-500">{l.start}</span><span className="w-1.5 h-1.5 shrink-0 rounded-full" style={{ backgroundColor: subjectStyle(l.subject).dot }} /><span className="truncate text-slate-800">{l.subject}</span></li>
          ))}
          {(d.lessons ?? []).length === 0 && <li className="text-xs text-slate-500">No lessons</li>}
        </ul>
      )}
    </section>
  );
};

export default function Week() {
  const [offset, setOffset] = useState(0);
  const [w, setW] = useState(null);
  useEffect(() => { setW(null); api.get(`/week?offset=${offset}`).then((r) => setW(r.data ?? null)).catch(() => setW(false)); }, [offset]);
  const hw = Array.isArray(w?.homework) ? w.homework : [];
  return (
    <main data-testid="week-page" className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-gold-300">Week view</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl text-slate-900 leading-none">{w?.monday ? `Week of ${fmtDue(w.monday)}` : "Your week"}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="week-prev" onClick={() => setOffset(offset - 1)} aria-label="Previous week" className="grid place-items-center w-10 h-10 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="week-this" onClick={() => setOffset(0)} className="h-10 rounded-xl border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100">This week</button>
          <button data-testid="week-next" onClick={() => setOffset(offset + 1)} aria-label="Next week" className="grid place-items-center w-10 h-10 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      {w === null && <div className="h-60 rounded-2xl bg-surface animate-pulse" />}
      {w === false && <p className="text-slate-500">Couldn't load this week.</p>}
      {w && <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">{w.days.map((d) => <DayCol key={d.date} d={d} hw={hw.filter((h) => h.due === d.date)} />)}</div>}
    </main>
  );
}
